import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  buildOutOfBudgetBoostSavePayload,
  parseOutOfBudgetBoostSettings,
  validateOutOfBudgetBoostForm,
  type OutOfBudgetBoostIncreaseMode,
  type OutOfBudgetBoostSavePayload,
  type OutOfBudgetBoostSettings,
} from '@/utils/outOfBudgetBoost';

function formFromSettings(initial: OutOfBudgetBoostSettings | null) {
  const settings = initial ? parseOutOfBudgetBoostSettings(initial) : null;
  return {
    enabled: settings?.enabled ?? false,
    increaseMode: settings?.increaseMode ?? ('PERCENT' as OutOfBudgetBoostIncreaseMode),
    increaseValue: settings?.increaseValue != null ? String(settings.increaseValue) : '',
    ceilingBudget: settings?.ceilingBudget != null ? String(settings.ceilingBudget) : '',
  };
}

export function OutOfBudgetBoostModal({
  entityName,
  scopeLabel,
  initial,
  loading,
  saving,
  error,
  onClose,
  onSave,
}: {
  entityName: string;
  scopeLabel: 'campaign' | 'portfolio';
  initial: OutOfBudgetBoostSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: OutOfBudgetBoostSavePayload) => void;
}) {
  const starting = formFromSettings(initial);
  const [enabled, setEnabled] = useState(starting.enabled);
  const [increaseMode, setIncreaseMode] = useState<OutOfBudgetBoostIncreaseMode>(
    starting.increaseMode
  );
  const [increaseValue, setIncreaseValue] = useState(starting.increaseValue);
  const [ceilingBudget, setCeilingBudget] = useState(starting.ceilingBudget);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const next = formFromSettings(initial);
    setEnabled(next.enabled);
    setIncreaseMode(next.increaseMode);
    setIncreaseValue(next.increaseValue);
    setCeilingBudget(next.ceilingBudget);
    setFormError(null);
  }, [initial]);

  const busy = saving || loading;
  const amountLabel = increaseMode === 'PERCENT' ? 'Increase percent' : 'Increase amount';

  function handleSave() {
    const message = validateOutOfBudgetBoostForm({
      increaseMode,
      increaseValue,
      ceilingBudget,
    });
    if (message) {
      setFormError(message);
      return;
    }
    setFormError(null);
    onSave(
      buildOutOfBudgetBoostSavePayload({
        enabled,
        increaseMode,
        increaseValue,
        ceilingBudget,
      })
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-lg flex-col rounded-xl bg-surface p-5 shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="pr-8 text-base font-semibold text-foreground">Out of budget</h3>
        <p className="mt-1 text-sm text-foreground-muted">{entityName}</p>
        <div className="mt-4 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading settings
            </div>
          ) : (
            <>
              <p className="text-sm text-foreground-muted">
                When this {scopeLabel} goes out of budget, increase the budget by the percent or
                amount below. The new budget will not go above the ceiling.
              </p>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={busy}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Increase budget when out of budget
              </label>
              <div>
                <label
                  htmlFor="out-of-budget-increase-mode"
                  className="block text-sm font-medium text-foreground"
                >
                  Increase by
                </label>
                <select
                  id="out-of-budget-increase-mode"
                  className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
                  value={increaseMode}
                  disabled={busy}
                  onChange={(e) =>
                    setIncreaseMode(e.target.value as OutOfBudgetBoostIncreaseMode)
                  }
                >
                  <option value="PERCENT">Percent</option>
                  <option value="VALUE">Fixed amount</option>
                </select>
              </div>
              <label className="block text-sm font-medium text-foreground">
                {amountLabel}
                <Input
                  className="mt-1"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={increaseValue}
                  disabled={busy}
                  onChange={(e) => setIncreaseValue(e.target.value)}
                />
              </label>
              <label className="block text-sm font-medium text-foreground">
                Ceiling budget
                <Input
                  className="mt-1"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={ceilingBudget}
                  disabled={busy}
                  onChange={(e) => setCeilingBudget(e.target.value)}
                />
              </label>
            </>
          )}

          {formError || error ? <p className="text-sm text-error">{formError || error}</p> : null}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={busy || loading} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
